import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider } from 'styled-components';

import App from './App';
import { theme } from './styles/theme';
import { GlobalStyle } from './styles/globalStyle';
import AppErrorBoundary from './components/common/AppErrorBoundary';
import { getEnv } from './config/env';
import { setTelemetryAdapter } from './lib/telemetry';
import { setLoggerSink } from './lib/logger';
import { createAppInsightsAdapter } from './lib/appInsightsAdapter';

import './styles.css';

// Defensive JSON serialisation for the logger bridge. The sink runs
// inside componentDidCatch on the error path, so we can't let
// JSON.stringify throw on a circular ref or BigInt and mask the
// original error.
const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserialisable]';
  }
};

// Initialise telemetry BEFORE rendering. When the connection-string env
// var is unset (dev/test default) telemetry stays a no-op and the
// logger keeps writing to console — see lib/telemetry + lib/logger.
const env = getEnv();
if (env.appInsightsConnectionString) {
  const adapter = createAppInsightsAdapter(env.appInsightsConnectionString);
  setTelemetryAdapter(adapter);

  // Bridge the logger to telemetry. Errors become trackException so
  // they're surfaced as exceptions in the AppInsights UI; the rest
  // become trackEvent calls keyed by severity so they're searchable.
  // `debug` is intentionally a no-op in production: if logger.debug
  // ever lands in a hot loop (render path, drag handler) we'd burn
  // AppInsights ingestion quota for nothing. The dev default sink
  // still routes debug to console for local work.
  setLoggerSink({
    debug: () => {},
    info: (message, ...args) => adapter.trackEvent('info', { message, args: safeStringify(args) }),
    warn: (message, ...args) => adapter.trackEvent('warn', { message, args: safeStringify(args) }),
    error: (message, errOrArg) => {
      if (errOrArg instanceof Error) {
        adapter.trackException(errOrArg, { message });
      } else {
        adapter.trackEvent('error', { message, extra: safeStringify(errOrArg) });
      }
    },
  });
}

const rootElement = document.getElementById('root');

if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <GlobalStyle />
        <AppErrorBoundary>
          <App />
        </AppErrorBoundary>
      </ThemeProvider>
    </React.StrictMode>,
  );
}
